import {
  BarChart3,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Clock3,
  Download,
  RefreshCw,
  Tags,
} from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { CsvExportProgress } from '../../components/CsvExportProgress';
import { useCsvExport } from '../../csvExport';
import type { ReasonCategory, ReasonLeaderboardData } from '../types';

interface ReasonLeaderboardProps {
  data: ReasonLeaderboardData | null;
  loading: boolean;
  updatedAtMs?: number | null;
  onRefresh?: () => void;
}

function formatMilliseconds(value: number): string {
  const hours = value / 3_600_000;
  return `${hours.toFixed(hours >= 10 ? 1 : 2)} h`;
}

function formatSeconds(value: number): string {
  const hours = value / 3600;
  return `${hours.toFixed(hours >= 10 ? 1 : 2)} h`;
}

function visibleCategories(data: ReasonLeaderboardData | null): ReasonCategory[] {
  if (!data) return [];
  return data.categories.filter((category) => {
    if (category.taxonomy_version === 2 && category.enabled) return true;
    return data.rows.some((row) => (row.category_distribution[category.code] ?? 0) > 0);
  });
}

const DAY_MS = 24 * 60 * 60 * 1000;
const SHANGHAI_OFFSET_MS = 8 * 60 * 60 * 1000;
const DEFAULT_EXPORT_RANGE_DAYS = 7;
const MAX_EXPORT_RANGE_DAYS = 90;

function shanghaiDateInputValue(ms: number): string {
  return new Date(ms + SHANGHAI_OFFSET_MS).toISOString().slice(0, 10);
}

function addShanghaiDays(dateValue: string, days: number): string {
  const parts = dateValue.split('-');
  if (parts.length !== 3) return '';
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (![year, month, day].every(Number.isFinite)) return '';
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

function shanghaiDayStartMs(dateValue: string): number {
  const parts = dateValue.split('-');
  if (parts.length !== 3) return Number.NaN;
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (![year, month, day].every(Number.isFinite)) return Number.NaN;
  return Date.UTC(year, month - 1, day) - SHANGHAI_OFFSET_MS;
}

function defaultExportDateRange(nowMs: number): { from: string; to: string } {
  const today = shanghaiDateInputValue(nowMs);
  return {
    from: addShanghaiDays(today, -(DEFAULT_EXPORT_RANGE_DAYS - 1)),
    to: today,
  };
}

function selectedExportRange(
  fromDate: string,
  toDate: string,
  nowMs: number
): { fromMs: number; toMs: number } | { error: string } {
  const fromMs = shanghaiDayStartMs(fromDate);
  const toDayStartMs = shanghaiDayStartMs(toDate);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toDayStartMs)) {
    return { error: 'Choose both completion dates.' };
  }
  const today = shanghaiDateInputValue(nowMs);
  const toMs = toDate === today ? nowMs : toDayStartMs + DAY_MS;
  if (toMs <= fromMs) return { error: 'The end date must be after the start date.' };
  if (toMs - fromMs > MAX_EXPORT_RANGE_DAYS * DAY_MS) {
    return { error: `The completion range cannot exceed ${MAX_EXPORT_RANGE_DAYS} days.` };
  }
  return { fromMs, toMs };
}

function csvCell(value: string | number): string {
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadLeaderboardCsv(data: ReasonLeaderboardData, categories: ReasonCategory[]): void {
  const options = categories.flatMap((category) => category.options);
  const header = [
    'operator',
    'completed_episodes',
    'labeled_segments',
    'reason_selections',
    ...categories.map((category) => `category_segments_${category.code}`),
    ...options.map((option) => `reason_selections_${option.code}`),
    'output_duration_s',
    'output_duration_h',
    'handling_elapsed_ms',
    'handling_elapsed_h',
  ];
  const rows = data.rows.map((row) => [
    row.operator,
    row.completed_tasks,
    row.labeled_segments,
    Object.values(row.reason_distribution).reduce((sum, count) => sum + count, 0),
    ...categories.map((category) => row.category_distribution[category.code] ?? 0),
    ...options.map((option) => row.reason_distribution[option.code] ?? 0),
    row.output_duration_s,
    row.output_duration_s / 3600,
    row.handling_elapsed_ms,
    row.handling_elapsed_ms / 3_600_000,
  ]);
  const csv = `\uFEFF${[header, ...rows]
    .map((row) => row.map((value) => csvCell(value)).join(','))
    .join('\r\n')}`;
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `seccapreason-leaderboard-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export function ReasonLeaderboard({
  data,
  loading,
  updatedAtMs = null,
  onRefresh = () => undefined,
}: ReasonLeaderboardProps) {
  const [expandedOperator, setExpandedOperator] = useState<string | null>(null);
  const initialRange = useMemo(() => defaultExportDateRange(Date.now()), []);
  const [rangeFrom, setRangeFrom] = useState(initialRange.from);
  const [rangeTo, setRangeTo] = useState(initialRange.to);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const categories = useMemo(() => visibleCategories(data), [data]);
  const columnCount = 6 + categories.length;
  const {
    exportState,
    exportPlan,
    completedParts,
    failedPart,
    isExporting,
    planCsvExport,
    downloadPlannedCsv,
  } = useCsvExport();
  const todayInput = shanghaiDateInputValue(Date.now());

  const runWorkResultsExport = async (range: { fromMs: number; toMs: number } | null) => {
    const label = 'SecCapReason work results';
    try {
      const plan = await planCsvExport({
        path: '/admin/work-results/export-plan',
        label,
        ...(range ? { query: { from_ms: String(range.fromMs), to_ms: String(range.toMs) } } : {}),
      });
      await downloadPlannedCsv({
        plan,
        csvPath: '/admin/work-results.csv',
        label,
      });
    } catch {
      // CsvExportProgress keeps the structured server detail visible for retry.
    }
  };

  const handleSelectedRangeExport = () => {
    if (isExporting) return;
    const range = selectedExportRange(rangeFrom, rangeTo, Date.now());
    if ('error' in range) {
      setRangeError(range.error);
      return;
    }
    setRangeError(null);
    void runWorkResultsExport(range);
  };

  const handleAllTimeExport = () => {
    if (isExporting) return;
    setRangeError(null);
    void runWorkResultsExport(null);
  };

  const handleRetryFailedPart = () => {
    if (!exportPlan || failedPart == null || isExporting) return;
    void downloadPlannedCsv({
      plan: exportPlan,
      csvPath: '/admin/work-results.csv',
      label: 'SecCapReason work results',
      completedParts,
    });
  };

  return (
    <main className="mx-auto w-full max-w-[1400px] px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-brand-text-primary">Leaderboard</h1>
        <div className="flex items-center gap-2">
          <span className="text-xs text-brand-text-secondary">
            {updatedAtMs == null
              ? 'Not refreshed'
              : `Updated ${new Date(updatedAtMs).toLocaleTimeString()}`}
          </span>
          <button
            type="button"
            disabled={loading}
            onClick={onRefresh}
            title="Refresh leaderboard"
            aria-label="Refresh leaderboard"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-brand-border text-brand-text-primary disabled:opacity-40"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            disabled={loading || isExporting || !data || data.rows.length === 0}
            title="Export summary CSV"
            aria-label="Export summary CSV"
            onClick={() => {
              if (data) downloadLeaderboardCsv(data, categories);
            }}
            className="inline-flex h-9 w-9 items-center justify-center gap-2 rounded-md border border-brand-border text-sm text-brand-text-primary disabled:opacity-40 sm:w-auto sm:px-3"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Export summary CSV</span>
          </button>
          <button
            type="button"
            disabled={loading || isExporting || !data || data.rows.length === 0}
            title="Export work results"
            aria-label="Export work results"
            onClick={handleSelectedRangeExport}
            className="inline-flex h-9 w-9 items-center justify-center gap-2 rounded-md border border-brand-border text-sm text-brand-text-primary disabled:opacity-40 sm:w-auto sm:px-3"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Export work results</span>
          </button>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-end gap-3 rounded-lg border border-brand-border bg-brand-surface p-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-brand-text-primary">
          <CalendarDays className="h-4 w-4 text-brand-orange" />
          Completion range
        </div>
        <label className="flex flex-col gap-1 text-xs text-brand-text-secondary">
          From
          <input
            aria-label="Completion range from"
            type="date"
            value={rangeFrom}
            max={todayInput}
            disabled={isExporting}
            onChange={(event) => {
              setRangeFrom(event.target.value);
              setRangeError(null);
            }}
            className="rounded-md border border-brand-border bg-brand-dark px-2.5 py-1.5 text-xs text-brand-text-primary focus:border-brand-orange focus:outline-none disabled:opacity-50"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-brand-text-secondary">
          To
          <input
            aria-label="Completion range to"
            type="date"
            value={rangeTo}
            max={todayInput}
            disabled={isExporting}
            onChange={(event) => {
              setRangeTo(event.target.value);
              setRangeError(null);
            }}
            className="rounded-md border border-brand-border bg-brand-dark px-2.5 py-1.5 text-xs text-brand-text-primary focus:border-brand-orange focus:outline-none disabled:opacity-50"
          />
        </label>
        <button
          type="button"
          onClick={handleAllTimeExport}
          disabled={loading || isExporting || !data || data.rows.length === 0}
          className="inline-flex h-9 items-center gap-2 rounded-md border border-brand-border px-3 text-xs text-brand-text-primary disabled:opacity-40"
        >
          <Download className="h-3.5 w-3.5" />
          All time
        </button>
        <span className="text-[11px] text-brand-text-secondary">
          Shanghai time · date range up to 90 days · All time auto-splits
        </span>
        {rangeError && <p className="basis-full text-xs text-red-300">{rangeError}</p>}
      </div>
      <div className="mt-4">
        <CsvExportProgress state={exportState} />
      </div>
      {exportPlan && (
        <section className="mt-4 rounded-lg border border-brand-border bg-brand-surface p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-brand-text-primary">
                Work results export plan
              </h2>
              <p className="mt-1 text-xs text-brand-text-secondary">
                {exportPlan.from_ms == null ? 'All time' : 'Selected completion range'} ·{' '}
                {exportPlan.environment.toUpperCase()} · {exportPlan.parts.length.toLocaleString()}{' '}
                part
                {exportPlan.parts.length === 1 ? '' : 's'}
              </p>
            </div>
            {failedPart != null && !isExporting && (
              <button
                type="button"
                onClick={handleRetryFailedPart}
                className="inline-flex h-8 items-center gap-2 rounded-md border border-brand-orange px-3 text-xs font-semibold text-brand-orange disabled:opacity-40"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Retry failed part
              </button>
            )}
          </div>
          <div className="mt-3 grid gap-2 text-xs sm:grid-cols-4">
            <div>
              <span className="text-brand-text-secondary">Episodes</span>
              <strong className="ml-2 font-mono text-brand-text-primary">
                {exportPlan.episode_count.toLocaleString()}
              </strong>
            </div>
            <div>
              <span className="text-brand-text-secondary">Segments</span>
              <strong className="ml-2 font-mono text-brand-text-primary">
                {exportPlan.segment_count.toLocaleString()}
              </strong>
            </div>
            <div>
              <span className="text-brand-text-secondary">Selections</span>
              <strong className="ml-2 font-mono text-brand-text-primary">
                {exportPlan.selection_count.toLocaleString()}
              </strong>
            </div>
            <div>
              <span className="text-brand-text-secondary">Per-file limit</span>
              <strong className="ml-2 font-mono text-brand-text-primary">
                {exportPlan.max_segments_per_file.toLocaleString()}
              </strong>
            </div>
          </div>
          <ol className="mt-4 space-y-2 text-xs">
            {exportPlan.parts.map((part) => {
              const completed = completedParts.includes(part.part);
              const failed = failedPart === part.part;
              const active = exportState?.part === part.part && isExporting;
              return (
                <li
                  key={part.part}
                  className="flex flex-wrap items-center justify-between gap-2 border-t border-brand-border/70 pt-2"
                >
                  <span className="text-brand-text-primary">
                    Part {part.part} · {part.segment_count.toLocaleString()} segments
                  </span>
                  <span className="flex min-w-0 items-center gap-2 text-brand-text-secondary">
                    <span className="max-w-[min(60vw,32rem)] truncate" title={part.filename}>
                      {part.filename}
                    </span>
                    <span
                      className={
                        failed
                          ? 'text-red-300'
                          : completed
                            ? 'text-emerald-300'
                            : 'text-brand-text-secondary'
                      }
                    >
                      {failed
                        ? 'Failed'
                        : completed
                          ? 'Complete'
                          : active
                            ? 'Downloading'
                            : 'Pending'}
                    </span>
                  </span>
                </li>
              );
            })}
          </ol>
        </section>
      )}
      <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-lg border border-brand-border bg-brand-surface p-4">
          <div className="flex items-center gap-2 text-xs uppercase text-brand-text-secondary">
            <BarChart3 className="h-4 w-4" /> Completed episodes
          </div>
          <div className="mt-2 text-2xl font-semibold text-brand-text-primary">
            {(data?.completed_tasks ?? 0).toLocaleString()}
          </div>
        </div>
        <div className="rounded-lg border border-brand-border bg-brand-surface p-4">
          <div className="flex items-center gap-2 text-xs uppercase text-brand-text-secondary">
            <Tags className="h-4 w-4" /> Labeled fail segments
          </div>
          <div className="mt-2 text-2xl font-semibold text-brand-text-primary">
            {(data?.labeled_segments ?? 0).toLocaleString()}
          </div>
        </div>
        <div className="rounded-lg border border-brand-border bg-brand-surface p-4">
          <div className="flex items-center gap-2 text-xs uppercase text-brand-text-secondary">
            <Tags className="h-4 w-4" /> Reason selections
          </div>
          <div className="mt-2 text-2xl font-semibold text-brand-text-primary">
            {(data?.reason_selections ?? 0).toLocaleString()}
          </div>
        </div>
        <div className="rounded-lg border border-brand-border bg-brand-surface p-4">
          <div className="flex items-center gap-2 text-xs uppercase text-brand-text-secondary">
            <Clock3 className="h-4 w-4" /> Output duration
          </div>
          <div className="mt-2 text-2xl font-semibold text-brand-text-primary">
            {formatSeconds(data?.output_duration_s ?? 0)}
          </div>
        </div>
      </div>

      <div className="mt-5 max-w-full overflow-x-auto rounded-lg border border-brand-border bg-brand-surface">
        <table className="w-full min-w-[1080px] text-left text-sm">
          <thead className="border-b border-brand-border bg-brand-surface-highlight/50 text-xs uppercase text-brand-text-secondary">
            <tr>
              <th className="px-4 py-3 font-medium">Operator</th>
              <th className="px-4 py-3 text-right font-medium">Episodes</th>
              <th className="px-4 py-3 text-right font-medium">Labeled segments</th>
              {categories.map((category) => (
                <th
                  key={category.code}
                  title="Segments with at least one reason in this category"
                  className="px-4 py-3 text-right font-medium"
                >
                  {category.label}
                  <span className="block normal-case text-[10px] font-normal">
                    Category segments
                  </span>
                </th>
              ))}
              <th className="px-4 py-3 text-right font-medium">
                <span className="inline-flex items-center gap-1">
                  <Clock3 className="h-3.5 w-3.5" /> Output duration
                </span>
              </th>
              <th className="px-4 py-3 text-right font-medium">Handling elapsed</th>
              <th className="w-12 px-2 py-3">
                <span className="sr-only">Details</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={columnCount}
                  className="px-4 py-12 text-center text-brand-text-secondary"
                >
                  Loading leaderboard
                </td>
              </tr>
            ) : (data?.rows.length ?? 0) === 0 ? (
              <tr>
                <td
                  colSpan={columnCount}
                  className="px-4 py-12 text-center text-brand-text-secondary"
                >
                  No completed work
                </td>
              </tr>
            ) : (
              data?.rows.map((row) => {
                const expanded = expandedOperator === row.operator;
                return (
                  <Fragment key={row.operator}>
                    <tr className="border-b border-brand-border/70">
                      <td className="px-4 py-3 font-mono text-xs text-brand-text-primary">
                        {row.operator}
                      </td>
                      <td className="px-4 py-3 text-right text-brand-text-secondary">
                        {row.completed_tasks}
                      </td>
                      <td className="px-4 py-3 text-right text-brand-text-secondary">
                        {row.labeled_segments}
                      </td>
                      {categories.map((category) => (
                        <td
                          key={category.code}
                          className="px-4 py-3 text-right text-brand-text-secondary"
                        >
                          {row.category_distribution[category.code] ?? 0}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right text-brand-text-secondary">
                        {formatSeconds(row.output_duration_s)}
                      </td>
                      <td
                        className="px-4 py-3 text-right text-brand-text-secondary"
                        title="Wall-clock time from assignment to first commit"
                      >
                        {formatMilliseconds(row.handling_elapsed_ms)}
                      </td>
                      <td className="px-2 py-3 text-right">
                        <button
                          type="button"
                          aria-label={expanded ? 'Hide reason details' : 'Show reason details'}
                          aria-expanded={expanded}
                          onClick={() => setExpandedOperator(expanded ? null : row.operator)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-brand-text-secondary hover:bg-brand-surface-highlight hover:text-brand-text-primary"
                        >
                          {expanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      </td>
                    </tr>
                    {expanded ? (
                      <tr className="border-b border-brand-border/70 bg-brand-dark/40">
                        <td colSpan={columnCount} className="px-4 py-4">
                          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                            {categories.map((category) => (
                              <section key={category.code}>
                                <h2 className="text-xs font-semibold text-brand-text-primary">
                                  {category.label}
                                </h2>
                                <p className="mt-1 text-[11px] text-brand-text-secondary">
                                  Reason selections
                                </p>
                                <dl className="mt-2 space-y-1.5 text-xs">
                                  {category.options.map((option) => (
                                    <div
                                      key={option.code}
                                      className="flex items-start justify-between gap-4"
                                    >
                                      <dt className="text-brand-text-secondary">{option.label}</dt>
                                      <dd className="font-mono text-brand-text-primary">
                                        {row.reason_distribution[option.code] ?? 0}
                                      </dd>
                                    </div>
                                  ))}
                                </dl>
                              </section>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </main>
  );
}
